import React from 'react';
import { Skel } from './Skeleton';
import { ChevronDown } from 'lucide-react';

/**
 * The filter vocabulary every analysis page shares (Apal 2026-10-15: "find
 * consistency of filters and making it look like high class UI"). Same
 * label, same 36px track, same pill, same select — Compare, Signal Analysis,
 * Quarterly Wins and the Allocation lab render filters only through these
 * pieces and SegmentControl, so the pages can't drift apart again.
 */
export const FILTER_LABEL = 'text-dark-400 text-[11px] uppercase tracking-widest font-semibold block mb-1.5';
export const FILTER_SELECT = 'h-9 bg-dark-700 border border-dark-600 rounded-lg px-2.5 text-[13px] font-semibold text-white cursor-pointer hover:border-dark-500 focus:outline-none focus:ring-1 focus:ring-primary-500';
export const FILTER_ROW = 'flex flex-wrap items-end gap-x-6 gap-y-4';

/**
 * Every filter dropdown: the native arrow is hidden (it rendered grey and
 * clipped — Apal 2026-10-16: "why the arrow looks so bad") and the SAME
 * chevron as the Year pill is drawn in the text's own colour.
 */
export const FilterSelect = ({ className = '', chevronClass = 'text-dark-300', wrapClass = '', compact = false, children, ...props }) => (
  <span className={`relative inline-flex ${wrapClass}`}>
    <select {...props}
      className={`${FILTER_SELECT} appearance-none ${compact ? 'h-8 pl-2.5 pr-7' : 'pr-8'} ${className}`}>
      {children}
    </select>
    <ChevronDown aria-hidden="true"
      className={`pointer-events-none absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${compact ? 'right-2' : 'right-2.5'} ${chevronClass}`} />
  </span>
);
const TRACK = 'h-9 flex items-stretch gap-1 bg-dark-700 p-1 rounded-lg border border-dark-600/30';
const PILL = 'px-[9px] text-[13px] flex items-center rounded-md font-semibold transition-all whitespace-nowrap';
const ON = 'bg-primary-600 text-white shadow-md';
const OFF = 'text-dark-300 hover:text-white hover:bg-dark-600';

/** "▸ Select indexes (N)" — the one header that opens a page's index chips. */
export const SelectorToggle = ({ open, onToggle, count, label = 'Select indexes' }) => (
  <button type="button" onClick={onToggle} aria-expanded={open}
    className="flex items-center gap-2 text-sm font-semibold text-white mb-2 max-w-full">
    <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${open ? '' : '-rotate-90'}`} />
    {/* count null = the selection isn't known yet: no number before it is one */}
    <span className="shrink-0">{label}{count == null ? null : ` (${count})`}</span>
    {count == null && <Skel className="h-3.5 w-6" testId="selector-count-pending" />}
  </button>
);

/**
 * Period — exactly one choice is lit: a preset (All history, 6M, 1Y …), a
 * Year pill that opens the year list and then names its year, or Custom,
 * whose own inputs (dates or years, the page's call) appear beside it.
 */
export const PeriodControl = ({
  id, testid, mode, presets, onPreset,
  years, pickedYear, onYear, yearTestid, yearLabelTestid, yearPillTestid, yearAriaLabel = 'Year',
  onCustom, custom = null, customYears = null, trailing = null,
}) => (
  <div>
    <span className={FILTER_LABEL} id={id}>Period</span>
    <div className="flex items-center gap-2" role="group" aria-labelledby={id} data-testid={testid}>
      <div className={TRACK}>
        {presets.map(p => (
          <button key={p.key} type="button" onClick={() => onPreset(p.key)} aria-pressed={mode === p.key}
            data-period-mode={p.key} title={p.title || ''}
            className={`${PILL} ${mode === p.key ? ON : OFF}`}>
            {p.label}
          </button>
        ))}
        <label data-period-mode="year" data-testid={yearPillTestid}
          className={`relative ${PILL} gap-1 cursor-pointer focus-within:ring-1 focus-within:ring-primary-400 ${mode === 'year' ? ON : OFF}`}>
          <span data-testid={yearLabelTestid}>{mode === 'year' && pickedYear ? pickedYear : 'Year'}</span>
          <ChevronDown aria-hidden="true" className="w-3.5 h-3.5 opacity-70" />
          <select data-testid={yearTestid} aria-label={yearAriaLabel} value={mode === 'year' && pickedYear ? pickedYear : ''}
            onChange={e => { if (e.target.value) onYear(Number(e.target.value)); }}
            className="absolute inset-0 w-full opacity-0 cursor-pointer">
            <option value="" disabled>Year</option>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </label>
        {mode === 'custom' && customYears ? (
          // Custom names its own range, like Year names its year: two year
          // pickers inside the lit pill, so Custom never widens the bar
          <div data-period-mode="custom" data-lit="true" role="group" aria-label="Custom years"
            className={`${PILL} ${ON} gap-0 !px-1`}>
            <InPillYear testid={customYears.fromTestid} label="From year" value={customYears.from} onChange={customYears.onFrom}
              years={customYears.years.filter(y => !customYears.to || Number(y) <= Number(customYears.to))} />
            <span aria-hidden="true" className="opacity-70">–</span>
            <InPillYear testid={customYears.toTestid} label="To year" value={customYears.to} onChange={customYears.onTo}
              years={customYears.years.filter(y => !customYears.from || Number(y) >= Number(customYears.from))} />
          </div>
        ) : (
          <button type="button" onClick={onCustom} aria-pressed={mode === 'custom'} data-period-mode="custom"
            className={`${PILL} ${mode === 'custom' ? ON : OFF}`}>
            Custom
          </button>
        )}
      </div>
      {mode === 'custom' && custom && <div className="h-9 flex items-center gap-2">{custom}</div>}
      {trailing}
    </div>
  </div>
);

const InPillYear = ({ testid, label, value, onChange, years }) => (
  <span className="relative flex items-center rounded px-[3px] underline decoration-white/40 decoration-dotted underline-offset-4 hover:bg-white/10 focus-within:ring-1 focus-within:ring-white/60">
    <span className="tabular-nums">{value}</span>
    <select data-testid={testid} aria-label={label} value={value} onChange={e => onChange(e.target.value)}
      className="absolute inset-0 w-full opacity-0 cursor-pointer">
      {years.map(y => <option key={y} value={y}>{y}</option>)}
    </select>
  </span>
);

/** From–to pair for Custom, years or dates, styled like every other select. */
export const RangeInputs = ({ kind = 'year', from, to, onFrom, onTo, years = [], fromTestid, toTestid }) => {
  if (kind === 'date') {
    return (
      <>
        <input type="date" value={from} onChange={e => onFrom(e.target.value)} aria-label="From date" data-testid={fromTestid}
          className={`${FILTER_SELECT} px-2`} />
        <span className="text-dark-500 text-sm">to</span>
        <input type="date" value={to} onChange={e => onTo(e.target.value)} aria-label="To date" data-testid={toTestid}
          className={`${FILTER_SELECT} px-2`} />
      </>
    );
  }
  return (
    <>
      <FilterSelect data-testid={fromTestid} aria-label="From year" value={from} onChange={e => onFrom(e.target.value)}>
        {years.filter(y => !to || Number(y) <= Number(to)).map(y => <option key={y} value={y}>{y}</option>)}
      </FilterSelect>
      <span className="text-dark-500 text-sm">to</span>
      <FilterSelect data-testid={toTestid} aria-label="To year" value={to} onChange={e => onTo(e.target.value)}>
        {years.filter(y => !from || Number(y) >= Number(from)).map(y => <option key={y} value={y}>{y}</option>)}
      </FilterSelect>
    </>
  );
};

/**
 * An On/Off switch with its label above (or beside it, `inline`, in a row of
 * buttons) — the same 36px height as every other filter. `onText`/`offText`
 * say the state in words.
 */
export const SwitchControl = ({ label, checked, onChange, title, testid, ariaLabel, onText = 'On', offText = 'Off', inline = false }) => (
  <div data-testid={testid} className={inline ? 'flex items-center gap-2.5' : ''}>
    <span className={inline ? `${FILTER_LABEL} !mb-0` : FILTER_LABEL}>{label}</span>
    <label title={title}
      className={`flex items-center gap-2 h-9 cursor-pointer select-none px-2.5 rounded-lg border transition-all ${checked ? 'bg-emerald-500/10 border-emerald-500/40' : 'bg-dark-700 border-dark-600 hover:border-dark-500'}`}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} className="sr-only" aria-label={ariaLabel || label} />
      <div className={`relative w-8 h-[18px] rounded-full transition-colors ${checked ? 'bg-emerald-500' : 'bg-dark-600'}`}>
        <span className={`absolute top-[2px] h-[14px] w-[14px] rounded-full bg-white transition-transform shadow ${checked ? 'left-[15px]' : 'left-[2px]'}`} />
      </div>
      <span className={`text-[13px] font-semibold w-6 ${checked ? 'text-emerald-300' : 'text-dark-400'}`}>{checked ? onText : offText}</span>
    </label>
  </div>
);

/** Open trades — the shared switch. */
export const IncludeOpenToggle = ({ checked, onChange, title }) => (
  <SwitchControl label="Open trades" checked={checked} onChange={onChange} title={title}
    testid="include-open" ariaLabel="Include open trades" />
);

/**
 * A threshold picker (Highlight, Trades of at least): label above, one
 * dropdown — the same on every page, and narrow enough for the one-line bar.
 * Lit amber while it is doing something, so an active filter is never quiet.
 */
// inline: label beside the control, for a section header (like SwitchControl inline)
export const ThresholdSelect = ({ id, label, testid, selectTestid, value, onChange, options, active = false, title, inline = false }) => (
  <div data-testid={testid} className={inline ? 'flex items-center gap-2.5' : ''}>
    <label className={inline ? `${FILTER_LABEL} !mb-0` : FILTER_LABEL} htmlFor={id}>{label}</label>
    <FilterSelect id={id} data-testid={selectTestid} value={value} onChange={e => onChange(e.target.value)} title={title}
      wrapClass={inline ? '' : 'w-full'} chevronClass={active ? 'text-amber-200' : 'text-dark-300'}
      className={`${inline ? '' : 'w-full'} ${active ? '!border-amber-400/70 !text-amber-200 !bg-amber-400/10' : ''}`}>
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </FilterSelect>
  </div>
);

/**
 * One weight on one line: swatch · name · slider · %. The slider is a native
 * range (keyboard + screen reader for free) drawn by `.cb-range` in
 * index.css: a filled track in the index's own colour up to its weight.
 */
export const WeightSlider = ({ label, value, display, color = '#14b8a6', onChange, step = 5, handle = null, testid, title }) => (
  <div className="flex items-center gap-2.5 h-7" data-testid={testid} title={title}>
    {handle}
    <span className="w-2 h-2 rounded-sm shrink-0" style={{ background: color }} />
    <span className="w-24 shrink-0 truncate text-xs text-dark-200" title={label}>{label}</span>
    <input type="range" min={0} max={100} step={step} value={value} aria-label={`${label} weight`}
      draggable={false} onDragStart={e => e.preventDefault()}
      onChange={e => onChange(parseInt(e.target.value, 10))}
      className="cb-range flex-1 min-w-0"
      style={{ '--fill': `${Math.min(100, Math.max(0, value))}%`, '--c': color }} />
    <span className={`w-12 shrink-0 text-right text-xs font-semibold tabular-nums ${value > 0 ? 'text-white' : 'text-dark-500'}`}>{display ?? `${value}%`}</span>
  </div>
);

/** The mix at a glance — one bar, one colour per index, widths = weights. */
export const MixBar = ({ items }) => {
  const total = items.reduce((a, it) => a + Math.max(0, it.weight || 0), 0);
  return (
    <div className="flex h-2 w-full overflow-hidden rounded-full bg-dark-700" data-testid="mix-bar"
      aria-label={items.map(it => `${it.label} ${it.weight}%`).join(', ')} role="img">
      {total > 0 && items.filter(it => it.weight > 0).map(it => (
        <div key={it.key} title={`${it.label} ${it.weight}%`}
          style={{ width: `${(it.weight / Math.max(total, 100)) * 100}%`, background: it.color }}
          className="h-full first:rounded-l-full border-r border-dark-900/60 last:border-r-0" />
      ))}
    </div>
  );
};

/** Execution — one wording on every page (the global bar and the lab). */
export const EXECUTION_OPTIONS = [
  { key: 'same_day_close', label: 'Close', title: "Execute at the same day's close price" },
  { key: 'next_day_open', label: 'Next open', title: "Execute at the next trading day's open price (default)" },
  { key: 'next_day_close', label: 'Next close', title: "Execute at the next trading day's close price" },
];
